import { differenceInMinutes, format, parse } from 'date-fns';
import { z } from 'zod';
import { apiUrl, API_CONFIG } from '../config/api';
import type { EmployeeLoggedHours } from '../data/databaseSchema';
import type { ApiResponse } from './projectService';
import { apiClient } from '../lib/apiClient';
import {
  apiResponseSchema,
  timeEntryResponseSchema,
  timeEntryStatusResponseSchema,
  employeeProjectHoursSchema,
  employeeLoggedHoursSchema,
} from '../lib/schemas';
import { logger } from '../utils/logger';

export interface ProjectInfo {
  project_id: string;
  project_number: string;
  project_name: string;
}

export interface OrderInfo {
  order_id: string;
  order_number: string;
  comments: string;
}

export interface TaskInfo {
  task_id: string;
  task_code: string;
  task_name: string;
  billable: boolean;
}

export interface ProjectLogEntry {
  id: string;
  project_info: ProjectInfo;
  order_info: OrderInfo;
  task_info: TaskInfo;
  hours_logged: number;
  description: string;
  work_type?: string | null;
  billable: boolean;
}

export interface DayDetail {
  id: string;
  planned_hours: number;
  start_time: string;
  end_time: string;
  pause: number;
  project_log: ProjectLogEntry[] | null;
  status: string;
}

export interface TimeEntryResponse {
  success: boolean;
  message: string;
  'calendar-week': string;
  data: {
    days: Record<string, DayDetail>;
    status: string;
  };
}

export interface TimeEntryStatusResponse {
  success: boolean;
  message: string;
  data: {
    day: Array<{
      employee_id: string;
      date_of_year: string;
      calendar_week: string;
      status: string;
      daily_hours_booked?: number;
      total_project_hours_booked?: number;
      is_holiday: boolean;
      is_absence?: boolean;
      is_vacation?: boolean;
      is_bridge?: boolean;
      has_planned_hours?: boolean;
    }>;
    week: Record<string, string>;
  };
}

export interface CreateProjectLoggedHoursRequest {
  employee_id: string;
  project_id: string;
  order_id?: string;
  task_id: string;
  date_of_year: string;
  hours_worked: number;
  status: string;
  description?: string;
  work_type?: string | null;
  billable?: boolean;
}

export interface UpdateProjectLoggedHoursRequest {
  project_id: string;
  order_id?: string;
  task_id: string;
  hours_worked: number;
  status: string;
  description?: string;
  work_type?: string;
  billable?: boolean;
}

export interface CreateLoggedHoursResponse {
  success: boolean;
  message: string;
  data: EmployeeLoggedHours;
}

export interface DeleteTimeEntryResponse {
  success: boolean;
  message: string;
}

export interface UpdateLoggedHoursRequest {
  employee_id: string;
  start_time: string;
  end_time: string;
  pause_hours: number;
}

export interface CreateLoggedHoursRequest {
  employee_id: string;
  start_time: string;
  end_time: string;
  pause_hours: number;
  date_of_year: string;
}

export interface UpdateLoggedHoursResponse {
  success: boolean;
  message: string;
  data?: EmployeeLoggedHours;
}

export const timeEntryService = {
  async getTimeEntries(date: string, empId: string): Promise<TimeEntryResponse> {
    const result = await apiClient.get(apiUrl.hoursEntry(empId, { date }), {
      validate: timeEntryResponseSchema,
      deduplicate: true,
      context: 'getTimeEntries',
    });
    return result;
  },

  async getTimeEntriesStatus(empId: string, period: string): Promise<TimeEntryStatusResponse> {
    const result = await apiClient.get(apiUrl.employeeStatusTimeEntries(empId, `?period=${period}`), {
      validate: timeEntryStatusResponseSchema,
      deduplicate: true,
      context: 'getTimeEntriesStatus',
    });
    return result;
  },

  async createProjectLoggedHours(
    data: CreateProjectLoggedHoursRequest
  ): Promise<CreateLoggedHoursResponse> {
    const result = await apiClient.post(apiUrl.projectLoggedHours(), data, {
      validate: apiResponseSchema(employeeProjectHoursSchema),
      context: 'createProjectLoggedHours',
    });
    return {
      success: result.success,
      message: result.message || '',
      data: result.data,
    };
  },

  async updateProjectLoggedHours(
    id: string,
    data: UpdateProjectLoggedHoursRequest
  ): Promise<CreateLoggedHoursResponse> {
    const result = await apiClient.put(apiUrl.updateProjectLoggedHours(id), data, {
      validate: apiResponseSchema(employeeProjectHoursSchema),
      context: 'updateProjectLoggedHours',
    });
    return {
      success: result.success,
      message: result.message || '',
      data: result.data,
    };
  },

  async deleteTimeEntry(id: string): Promise<DeleteTimeEntryResponse> {
    const result = await apiClient.delete(apiUrl.deleteTimeEntry(id), {
      context: 'deleteTimeEntry',
    });
    return {
      success: result.success || true,
      message: result.message || '',
    };
  },

  async deleteLoggedHours(id: string): Promise<DeleteTimeEntryResponse> {
    const result = await apiClient.delete(apiUrl.loggedHours(id), {
      context: 'deleteLoggedHours',
    });
    return {
      success: result.success || true,
      message: result.message || '',
    };
  },

  async clearAllProjectLoggedHours(employeeId: string, date: string): Promise<DeleteTimeEntryResponse> {
    const result = await apiClient.delete(apiUrl.clearAllProjectLoggedHours(employeeId, date), {
      context: 'clearAllProjectLoggedHours',
    });
    return {
      success: result.success || true,
      message: result.message || '',
    };
  },

  async updateLoggedHours(
    id: string,
    data: UpdateLoggedHoursRequest
  ): Promise<UpdateLoggedHoursResponse> {
    logger.debug('updateLoggedHours apiUrl.loggedHours()', apiUrl.loggedHours(id));
    logger.debug('updateLoggedHours data', data);
    const result = await apiClient.put(apiUrl.loggedHours(id), data, {
      validate: apiResponseSchema(employeeLoggedHoursSchema.optional()),
      context: 'updateLoggedHours',
    });
    return {
      success: result.success,
      message: result.message || '',
      data: result.data,
    };
  },

  async createLoggedHours(data: CreateLoggedHoursRequest): Promise<CreateLoggedHoursResponse> {
    logger.debug('createLoggedHours apiUrl.loggedHours()', apiUrl.loggedHours());
    logger.debug('createLoggedHours data', data);
    const result = await apiClient.post(apiUrl.loggedHours(), data, {
      validate: apiResponseSchema(employeeLoggedHoursSchema),
      context: 'createLoggedHours',
    });
    return {
      success: result.success ?? true,
      message: result.message || '',
      data: result.data,
    };
  },

  async closeWeekEntries(employeeId: string, calendarWeek: string): Promise<WeekClosePlan> {
    const result = await apiClient.put(apiUrl.closeWeekEntries(employeeId, calendarWeek), undefined, {
      validate: apiResponseSchema(weekClosePlanSchema),
      context: 'closeWeekEntries',
    });
    return result.data;
  },

  async previewCloseWeek(employeeId: string, calendarWeek: string): Promise<WeekClosePlan> {
    const result = await apiClient.get(apiUrl.closeWeekPreview(employeeId, calendarWeek), {
      validate: apiResponseSchema(weekClosePlanSchema),
      deduplicate: false,
      context: 'previewCloseWeek',
    });
    return result.data;
  },
};

const weekClosePlanSchema = z.object({
  calendar_week: z.string(),
  mismatch_days: z.array(z.string()),
  missing_days: z.array(z.string()),
});

export type WeekClosePlan = z.infer<typeof weekClosePlanSchema>;

export interface LogStatusSummary {
  employee_id: string;
  employee_name: string;
  calendar_week: string;
  difference: boolean;
  status: string;
}

export interface LogProject {
  date: string;
  calendar_week: string;
  employee_id: string;
  employee_name: string;
  project: string;
  order: string;
  task: string;
  description: string;
  billable: boolean;
  hours: number;
  work_type?: string | null;
}

const logStatusSummarySchema = z.object({
  employee_id: z.string(),
  employee_name: z.string(),
  calendar_week: z.string(),
  difference: z.boolean(),
  status: z.string(),
});

const logProjectSchema = z.object({
  date: z.string(),
  calendar_week: z.string(),
  employee_id: z.string(),
  employee_name: z.string(),
  project: z.string(),
  order: z.string(),
  task: z.string(),
  description: z.string(),
  billable: z.boolean(),
  hours: z.number(),
  work_type: z.string().optional().nullable(),
});

export const getLogStatusSummary = async (calendarWeek?: string): Promise<LogStatusSummary[]> => {
  const result = await apiClient.get(apiUrl.adminLogStatus(calendarWeek), {
    validate: apiResponseSchema(z.array(logStatusSummarySchema)),
    deduplicate: true,
    context: 'getLogStatusSummary',
  });
  return result.data || [];
};

export const getLogProjects = async (): Promise<LogProject[]> => {
  const result = await apiClient.get(apiUrl.adminLogProjects(), {
    validate: apiResponseSchema(z.array(logProjectSchema)),
    deduplicate: true,
    context: 'getLogProjects',
  });
  return result.data || [];
};

export const getLogProjectsByEmployee = async (
  employeeId: string,
  month?: number,
  year?: number,
  projectNumber?: string
): Promise<LogProject[]> => {
  const result = await apiClient.get(apiUrl.employeeLogProjects(employeeId, month, year, projectNumber), {
    validate: apiResponseSchema(z.array(logProjectSchema)),
    deduplicate: true,
    context: 'getLogProjectsByEmployee',
  });
  return result.data || [];
};

async function downloadExcelExport(url: string, defaultFilename: string): Promise<void> {
  const fullUrl = url.startsWith('http') ? url : `${API_CONFIG.baseURL}${url}`;
  const response = await fetch(fullUrl, { credentials: 'include' });
  if (!response.ok) {
    const text = await response.text();
    let message = response.statusText;
    try {
      const json = JSON.parse(text);
      if (json?.error) message = json.error;
    } catch {
      if (text) message = text;
    }
    throw new Error(message);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  let filename = defaultFilename;
  if (disposition) {
    const match = /filename="?([^";\n]+)"?/.exec(disposition);
    if (match?.[1]) filename = match[1].trim();
  }
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(objectUrl);
}

export const exportLeistungsnachweisExcel = async (
  employeeId: string,
  year: number,
  month: number,
  projectNumber?: string,
  order?: string
): Promise<void> => {
  let url = `${apiUrl.employeeLogProjectsExportExcel(employeeId)}?year=${year}&month=${month}`;
  if (projectNumber != null && projectNumber !== '') {
    url += `&project=${encodeURIComponent(projectNumber)}`;
  }
  if (order != null && order !== '') {
    url += `&order=${encodeURIComponent(order)}`;
  }
  const defaultFilename = `Leistungsnachweis_${employeeId}_${year}-${String(month).padStart(2, '0')}.xlsx`;
  await downloadExcelExport(url, defaultFilename);
};

export const reOpenWeekEntries = async (
  employeeId: string,
  calendarWeek: string
): Promise<ApiResponse<null>> => {
  const url = `${API_CONFIG.apiVersion}/employee/emp_id/${employeeId}/entries/reopen?calendar_week=${encodeURIComponent(calendarWeek)}`;
  const result = await apiClient.put(url, undefined, {
    validate: apiResponseSchema(z.null().optional()),
    context: 'reOpenWeekEntries',
  });
  return {
    success: result.success ?? true,
    message: result.message || '',
    data: result.data ?? null,
  };
};

export const timeEntryServiceUtils = {
  calculateHoursBooked: (startTime: string, endTime: string, pauseMinutes: number): number => {
    if (!startTime || !endTime) {
      return 0;
    }

    const sanitizeTime = (time: string): string => time.replace(/[^0-9:]/g, '');
    const sanitizedStartTime = sanitizeTime(startTime);
    const sanitizedEndTime = sanitizeTime(endTime);

    if (!sanitizedStartTime || !sanitizedEndTime) {
      return 0;
    }

    const today = format(new Date(), 'yyyy-MM-dd');
    const start = parse(`${today} ${sanitizedStartTime}`, 'yyyy-MM-dd HH:mm', new Date());
    const end = parse(`${today} ${sanitizedEndTime}`, 'yyyy-MM-dd HH:mm', new Date());

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return 0;
    }

    const totalMinutes = differenceInMinutes(end, start);
    if (isNaN(totalMinutes)) {
      return 0;
    }

    const workingMinutes = totalMinutes - (pauseMinutes || 0);
    const hours = workingMinutes / 60;
    if (isNaN(hours)) {
      return 0;
    }
    const roundedHours = Math.round(Math.max(0, hours) * 100) / 100;
    return roundedHours;
  },
};

export interface OverviewTimeEntry {
  datum: string;
  tagname: string;
  projektnummer: string;
  projektname: string;
  bestellnummer: string;
  worktype: string;
  task: string;
  beschreibung: string;
  stunden: number;
  billable: boolean;
}

export const getEmployeeTimeEntriesOverview = async (
  year: number,
  employeeId: string
): Promise<LogProject[]> => {
  const result = await apiClient.get(apiUrl.adminLogProjects(), {
    validate: apiResponseSchema(z.array(logProjectSchema)),
    deduplicate: true,
    context: 'getEmployeeTimeEntriesOverview',
  });
  
  const allEntries = result.data || [];
  
  return allEntries.filter((entry: LogProject) => {
    if (!entry.date) return false;
    const entryYear = new Date(entry.date.split('.').reverse().join('-')).getFullYear();
    return entryYear === year && entry.employee_id === employeeId;
  });
};

export const getEmployeeDetailedTimeEntries = async (
  year: number,
  employeeId: string,
  month?: number,
  projectNumber?: string
): Promise<OverviewTimeEntry[]> => {
  const result = await apiClient.get(apiUrl.employeeProjectLogHours(employeeId, year, projectNumber ?? null), {
    validate: apiResponseSchema(z.array(logProjectSchema)),
    deduplicate: true,
    context: 'getEmployeeDetailedTimeEntries',
  });
  
  const entries: LogProject[] = result.data || [];
  
  const dayNames = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
  
  return entries
    .filter((entry: LogProject) => {
      if (!entry.date) return false;
      const dateParts = entry.date.split('.');
      if (dateParts.length !== 3) return false;
      const entryDate = new Date(dateParts.reverse().join('-'));
      
      if (isNaN(entryDate.getTime())) return false;
      
      if (month !== undefined && entryDate.getMonth() !== month) {
        return false;
      }
      
      return true;
    })
    .map((entry: LogProject) => {
      const entryDate = new Date(entry.date.split('.').reverse().join('-'));
      const dayName = dayNames[entryDate.getDay()];
      
      const projectMatch = entry.project.match(/^([^\s]+)(?:\s+(.+?))?(?:\s*\(([^)]+)\))?$/);
      const projektnummer = projectMatch ? projectMatch[1] : entry.project.split(' ')[0] || '';
      const projektname = projectMatch && projectMatch[2] ? projectMatch[2].trim() : '';
      
      const orderParts = entry.order.split(' ');
      const bestellnummer = orderParts[0] === '-' ? '' : (orderParts[0] || '');
      
      return {
        datum: entry.date,
        tagname: dayName,
        projektnummer,
        projektname,
        bestellnummer,
        worktype: entry.work_type || '',
        task: entry.task,
        beschreibung: entry.description,
        stunden: entry.hours,
        billable: entry.billable,
      };
    })
    .sort((a: OverviewTimeEntry, b: OverviewTimeEntry) => b.datum.localeCompare(a.datum));
};

export interface YearOverview {
  totalprojecthours: number;
  totalbillablehours: number;
  totalplannedtime: number;
  overallflextime: number;
  overallprojecthours: number;
  overallplannedtime: number;
  overallstartdate: string;
}

export interface MonthOverview {
  month: number;
  totalplannedhours: number;
  totalprojecthours: number;
  totalbillablehours: number;
}

export interface InternalProject {
  internalhours: number;
  absencehours: number;
}

export interface ExternalProject {
  projectnumber: string;
  projectname: string;
  totalbillablehours: number;
  totalnonbillablehours: number;
}

export interface ProjectOverview {
  internalProject: InternalProject;
  externalProject: ExternalProject[];
}

const yearOverviewSchema = z.object({
  totalprojecthours: z.number(),
  totalbillablehours: z.number(),
  totalplannedtime: z.number(),
  overallflextime: z.number(),
  overallprojecthours: z.number(),
  overallplannedtime: z.number(),
  overallstartdate: z.string(),
});

const monthOverviewSchema = z.object({
  month: z.number(),
  totalplannedhours: z.number(),
  totalprojecthours: z.number(),
  totalbillablehours: z.number(),
});

const internalProjectSchema = z.object({
  internalhours: z.number(),
  absencehours: z.number(),
});

const externalProjectSchema = z.object({
  projectnumber: z.string(),
  projectname: z.string(),
  totalbillablehours: z.number(),
  totalnonbillablehours: z.number(),
});

const projectOverviewSchema = z.object({
  internalProject: internalProjectSchema,
  externalProject: z.array(externalProjectSchema),
});

export const getEmployeeYearOverview = async (
  year: number,
  employeeId: string
): Promise<YearOverview> => {
  const result = await apiClient.get(apiUrl.employeeYearOverview(employeeId, year), {
    validate: apiResponseSchema(yearOverviewSchema),
    deduplicate: true,
    context: 'getEmployeeYearOverview',
  });
  return result.data;
};

export const getEmployeeMonthlyOverview = async (
  year: number,
  employeeId: string
): Promise<MonthOverview[]> => {
  const result = await apiClient.get(apiUrl.employeeMonthOverview(employeeId, year), {
    validate: apiResponseSchema(z.array(monthOverviewSchema)),
    deduplicate: true,
    context: 'getEmployeeMonthlyOverview',
  });
  return result.data || [];
};

export const getEmployeeProjectOverview = async (
  year: number,
  employeeId: string,
  month?: number | null
): Promise<ProjectOverview> => {
  const result = await apiClient.get(apiUrl.employeeProjectOverview(employeeId, year, month), {
    validate: apiResponseSchema(projectOverviewSchema),
    deduplicate: true,
    context: 'getEmployeeProjectOverview',
  });
  return result.data;
};